/** Modal that appears after a successful first-time password login on a device that
 *  doesn't yet have a PIN. Lets the user set a 6-digit PIN or skip.
 */
import { useState } from "react";
import PinPad from "@/components/PinPad";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Key, X, ShieldCheck } from "@phosphor-icons/react";

export default function SetupPinModal({ onClose, onSkip }) {
  const { setupPin } = useAuth();
  const [step, setStep] = useState(1); // 1 = choose pin, 2 = confirm
  const [pin1, setPin1] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);

  const onComplete1 = (p) => {
    setPin1(p);
    setStep(2);
  };

  const onComplete2 = async (p) => {
    if (p !== pin1) {
      toast.error("PINs don't match — try again");
      setPin2("");
      return;
    }
    setBusy(true);
    try {
      const label = `${navigator.userAgent.split(")")[0].split("(").pop() || "Browser"} — ${new Date().toLocaleDateString("en-AU", { timeZone: "Australia/Brisbane" })}`;
      await setupPin(p, label.slice(0, 120));
      toast.success("PIN saved — you can use it next time you sign in");
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save PIN");
      setStep(1); setPin1(""); setPin2("");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="setup-pin-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#f38838]/10 flex items-center justify-center">
              <Key size={22} weight="duotone" className="text-[#f38838]" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-bold text-stone-900">Set up quick sign-in</h2>
              <p className="text-xs text-stone-500">Use a 6-digit PIN instead of your password next time.</p>
            </div>
          </div>
          <button onClick={onSkip || onClose} className="p-1.5 text-stone-400 hover:bg-stone-100 rounded" data-testid="setup-pin-skip"><X size={18} /></button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-900 flex gap-2">
          <ShieldCheck size={16} weight="duotone" className="shrink-0 mt-0.5 text-amber-700" />
          <span>This PIN only works on <strong>this device/browser</strong>. Your password still works as a backup. Five wrong tries locks PIN for 15 minutes.</span>
        </div>

        {step === 1 ? (
          <>
            <p className="text-sm text-stone-700 text-center mb-4">Choose a 6-digit PIN</p>
            <PinPad value={pin1} onChange={setPin1} onComplete={onComplete1} disabled={busy} data-testid="setup-pin-pad-1" />
          </>
        ) : (
          <>
            <p className="text-sm text-stone-700 text-center mb-4">Confirm your PIN</p>
            <PinPad value={pin2} onChange={setPin2} onComplete={onComplete2} disabled={busy} data-testid="setup-pin-pad-2" />
            <button onClick={() => { setStep(1); setPin1(""); setPin2(""); }} className="mt-3 text-xs text-stone-500 hover:underline w-full text-center">Start over</button>
          </>
        )}

        <button onClick={onSkip || onClose} className="mt-5 w-full text-sm text-stone-500 hover:text-stone-700" data-testid="setup-pin-not-now">
          Not now — I'll set this up later from settings
        </button>
      </div>
    </div>
  );
}
